import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Network, Truck, Car, Hash, LogOut, ChevronDown } from 'lucide-react';
import { GatePassReceipt } from '../components/GatePassReceipt';
interface GateEntryPageProps {
  userName: string;
}
const vehicleTypes = ['Select vehicle type', 'Truck - Heavy', 'Truck - Light', 'Trailer', 'Container Carrier', 'Tanker', 'Mini Truck', 'Pickup Van', 'Car', 'Two Wheeler', 'Other'];
export function GateEntryPage({
  userName
}: GateEntryPageProps) {
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [vehicleType, setVehicleType] = useState('');
  const [showReceipt, setShowReceipt] = useState(false);
  const gatepassNumber = 'GP-2026-04-00127';
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowReceipt(true);
  };
  const now = new Date();
  const entryTime = now.toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  return <div className="w-full max-w-lg mx-auto" data-id="element-633">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 p-5 xs:p-6 sm:p-8" data-id="element-634">
        {/* Page Title */}
        <div className="mb-5 sm:mb-7" data-id="element-635">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight mb-1" data-id="element-636">
            Vehicle Gate Entry
          </h2>
          <p className="text-sm text-slate-500" data-id="element-637">
            Record vehicle details for gate pass processing.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5" data-id="element-638">
          {/* Gatepass Number - Disabled */}
          <div data-id="element-639">
            <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-640">
              Gatepass Number
            </label>
            <div className="relative" data-id="element-641">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-642">
                <Hash className="h-5 w-5 text-slate-300" data-id="element-643" />
              </div>
              <input type="text" value={gatepassNumber} disabled className="block w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl bg-slate-50 text-slate-500 font-mono text-base sm:text-sm min-h-[48px] cursor-not-allowed" data-id="element-644" />
              <div className="absolute inset-y-0 right-0 pr-3.5 flex items-center pointer-events-none" data-id="element-645">
                <span className="text-[10px] xs:text-xs font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md" data-id="element-646">
                  Auto-generated
                </span>
              </div>
            </div>
          </div>

          {/* Vehicle Number Field */}
          <div data-id="element-647">
            <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-648">
              Vehicle Number
            </label>
            <div className="relative" data-id="element-649">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-650">
                <Truck className="h-5 w-5 text-slate-400" data-id="element-651" />
              </div>
              <input type="text" value={vehicleNumber} onChange={e => setVehicleNumber(e.target.value.toUpperCase())} className="block w-full pl-11 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-brand-teal/20 focus:border-brand-teal text-slate-900 placeholder-slate-400 transition-all min-h-[48px] text-base sm:text-sm font-mono uppercase" placeholder="e.g. KA 01 AB 1234" required data-id="element-652" />
            </div>
          </div>

          {/* Vehicle Type Field */}
          <div data-id="element-653">
            <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-654">
              Vehicle Type
            </label>
            <div className="relative" data-id="element-655">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-656">
                <Car className="h-5 w-5 text-slate-400" data-id="element-657" />
              </div>
              <select value={vehicleType} onChange={e => setVehicleType(e.target.value)} className="block w-full pl-11 pr-10 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-brand-teal/20 focus:border-brand-teal text-slate-900 transition-all min-h-[48px] text-base sm:text-sm appearance-none bg-white cursor-pointer" required data-id="element-658">
                {vehicleTypes.map(type => <option key={type} value={type === 'Select vehicle type' ? '' : type} disabled={type === 'Select vehicle type'} data-id="element-659">
                    {type}
                  </option>)}
              </select>
              <div className="absolute inset-y-0 right-0 pr-3.5 flex items-center pointer-events-none" data-id="element-660">
                <ChevronDown className="h-5 w-5 text-slate-400" data-id="element-661" />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button type="submit" className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-base font-semibold text-white bg-brand-teal hover:bg-brand-tealHover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-teal transition-all min-h-[48px] mt-2 active:scale-[0.98]" data-id="element-662">
            Submit & Print Gate Pass
          </button>
        </form>
      </motion.div>

      {/* Receipt Modal */}
      {showReceipt && <GatePassReceipt gatepassNumber={gatepassNumber} vehicleNumber={vehicleNumber || 'N/A'} vehicleType={vehicleType || 'N/A'} entryTime={entryTime} operatorName={userName} onClose={() => setShowReceipt(false)} data-id="element-663" />}
    </div>;
}